import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, Loader2, Mail, Send, X, CheckCircle2 } from 'lucide-react';
import { apiPost } from '../shared/apiClient';
import { useToast } from '../hooks/useToast';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ isOpen, onClose }: ForgotPasswordModalProps) {
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ type: 'error', message: 'Inserisci la tua email' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiPost<{ message: string }>('/auth/request-reset-password', { email });
      setSentMessage(data.message || 'Istruzioni inviate via email se l\'indirizzo è registrato');
      setStep('reset');
    } catch (err: any) {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Errore durante la richiesta' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast({ type: 'error', message: 'Inserisci il codice / token ricevuto' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ type: 'error', message: 'La nuova password deve contenere almeno 6 caratteri' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ type: 'error', message: 'Le password non coincidono' });
      return;
    }

    setLoading(true);
    try {
      await apiPost('/auth/reset-password', { token, newPassword });
      toast({ type: 'success', message: 'Password reimpostata con successo! Ora puoi accedere.' });
      onClose();
      setStep('request');
      setEmail('');
      setToken('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Token non valido o scaduto' });
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
      <div className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
              🔑
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 text-base">Recupero Password</h3>
              <p className="text-xs text-slate-500">Invia istruzioni di reset via email</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost p-1 text-slate-400 hover:text-slate-600 rounded-xl"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'request' ? (
          <form onSubmit={handleRequest} className="space-y-3 text-xs">
            <p className="text-slate-600 leading-relaxed">
              Inserisci l'indirizzo email del tuo account. Ti invieremo le istruzioni ed il codice per impostare una nuova password.
            </p>
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Email Registrata</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="email"
                  className="input pl-9 text-xs font-semibold"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="la-tua-email@azienda.it"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button type="button" className="btn-ghost text-xs font-bold" onClick={() => setStep('reset')}>
                Hai già un codice?
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary py-2.5 px-4 text-xs font-bold gap-1.5 shadow-sm">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span>Invia Email Reset</span>
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleReset} className="space-y-3 text-xs">
            {sentMessage && (
              <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start gap-2 text-xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>{sentMessage}</span>
              </div>
            )}
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Codice / Token di Reset</label>
              <input
                type="text"
                className="input text-xs font-semibold"
                value={token}
                onChange={(e) => setToken(e.target.value.trim())}
                placeholder="Incolla il token o codice..."
                required
              />
            </div>
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Nuova Password</label>
              <input
                type="password"
                className="input text-xs font-semibold"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Almeno 6 caratteri..."
                required
              />
            </div>
            <div className="space-y-1">
              <label className="block font-bold text-slate-700">Conferma Nuova Password</label>
              <input
                type="password"
                className="input text-xs font-semibold"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti nuova password..."
                required
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button type="button" className="btn-ghost text-xs font-bold" onClick={() => setStep('request')}>
                ← Torna a Invia Email
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary py-2.5 px-4 text-xs font-bold gap-1.5 shadow-sm">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                <span>Salva Nuova Password</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
